import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import AddressBook from './AddressBook';
import userService from '../services/userService';
import { useLanguage } from '../context/LanguageContext';

export default function AccountWindow({ open, onClose, togglePrices, showPrices, initialTab = 'profile' }) {
  const { user, setUser } = useContext(AuthContext);
  const { t } = useLanguage();
  const [tab, setTab] = useState(initialTab);

  // Il menu utente apre la finestra direttamente sulla scheda scelta
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);
  const [sharingPending, setSharingPending] = useState(false);
  const [feedbackType, setFeedbackType] = useState('suggestion');
  const [feedbackPriority, setFeedbackPriority] = useState('medium');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackPending, setFeedbackPending] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState('');
  const [feedbackError, setFeedbackError] = useState('');

  if (!open) return null;

  const countryCode = (user?.country || '').toUpperCase();
  const countryLabelByCode = {
    IT: t('account.countryIt'),
    AT: t('account.countryAt'),
    DE: t('account.countryDe'),
    ES: t('account.countryEs'),
    FR: t('account.countryFr'),
  };
  const countryLabel = countryLabelByCode[countryCode] || t('account.countryOther');

  const handleToggleSharing = async () => {
    if (!user) return;
    const token = localStorage.getItem('token');
    setSharingPending(true);
    try {
      const next = !user.companySharingEnabled;
      await userService.updateCompanySharing(next, token);
      const updated = { ...user, companySharingEnabled: next };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
    } catch (err) {
      alert(t('account.sharingUpdateError'));
    } finally {
      setSharingPending(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const normalizedMessage = String(feedbackMessage || '').trim();
    if (!normalizedMessage) {
      setFeedbackError('Inserisci un messaggio prima di inviare.');
      setFeedbackSuccess('');
      return;
    }

    setFeedbackPending(true);
    setFeedbackError('');
    setFeedbackSuccess('');
    try {
      await userService.createFeedback(
        {
          type: feedbackType,
          priority: feedbackPriority,
          message: normalizedMessage,
          page: window?.location?.pathname || '',
        },
        token
      );
      setFeedbackMessage('');
      setFeedbackType('suggestion');
      setFeedbackPriority('medium');
      setFeedbackSuccess('Feedback inviato. Grazie per il contributo.');
    } catch (err) {
      setFeedbackError('Invio non riuscito. Riprova tra poco.');
    } finally {
      setFeedbackPending(false);
    }
  };

  return (
    <div className="aw-overlay" onClick={onClose}>
      <div className="aw-window" onClick={(e) => e.stopPropagation()}>
        <div className="aw-header">
          <h2>{t('account.title')}</h2>
          <button className="aw-close" onClick={onClose}>X</button>
        </div>

        <div className="aw-tabs">
          <button
            className={tab === 'profile' ? 'active' : ''}
            onClick={() => setTab('profile')}
          >
            {t('account.profile')}
          </button>

          {(user?.role === 'admin' || user?.quoteEnabled) && (
            <button
              className={tab === 'book' ? 'active' : ''}
              onClick={() => setTab('book')}
            >
              {t('account.addressBook')}
            </button>
          )}
          <button
            className={tab === 'feedback' ? 'active' : ''}
            onClick={() => setTab('feedback')}
          >
            Feedback
          </button>
        </div>

        <div className="aw-content">
          {tab === 'profile' && (
            <div className="aw-profile">
              <div className="aw-profile-grid">
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.name')}</span>
                  <span className="aw-field-value">{user?.name || '-'}</span>
                </div>
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.email')}</span>
                  <span className="aw-field-value">{user?.email || '-'}</span>
                </div>
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.phone')}</span>
                  <span className="aw-field-value">{user?.phoneNumber || '-'}</span>
                </div>
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.role')}</span>
                  <span className="aw-field-value">{user?.role || '-'}</span>
                </div>
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.discount')}</span>
                  <span className="aw-field-value">{user?.discount ?? 0}%</span>
                </div>
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.company')}</span>
                  <span className="aw-field-value">{user?.companyName || '-'}</span>
                </div>
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.country')}</span>
                  <span className="aw-field-value">{countryLabel}</span>
                </div>
                <div className="aw-field">
                  <span className="aw-field-label">{t('account.quotes')}</span>
                  <span className="aw-field-value">
                    {user?.quoteEnabled ? t('account.enabled') : t('account.disabled')}
                  </span>
                </div>
                <div className="aw-field aw-field-full">
                  <span className="aw-field-label">{t('account.companyKey')}</span>
                  <span className="aw-field-value">{user?.companyKey || '-'}</span>
                </div>
              </div>

              <div className="aw-settings">
                <div className="aw-toggle">
                  <label htmlFor="price-toggle">{t('account.showPrices')}</label>
                  <input
                    id="price-toggle"
                    type="checkbox"
                    checked={!!showPrices}
                    onChange={togglePrices}
                  />
                </div>
                <div className="aw-toggle">
                  <label htmlFor="sharing-toggle">{t('account.shareOrders')}</label>
                  <input
                    id="sharing-toggle"
                    type="checkbox"
                    checked={user?.companySharingEnabled !== false}
                    onChange={handleToggleSharing}
                    disabled={sharingPending}
                  />
                </div>
              </div>
              <div className="aw-contact">
                <span>{t('account.contactReferent')}</span>
                <a href="mailto:info@goooled.com">info@goooled.com</a>
              </div>

            </div>
          )}

          {tab === 'book' && <AddressBook />}
          {tab === 'feedback' && (
            <div className="aw-feedback">
              <h3>Commenti e suggerimenti</h3>
              <p className="aw-feedback-subtitle">
                Inviaci idee o problemi riscontrati: ci aiutano a migliorare il portale.
              </p>
              <form className="aw-feedback-form" onSubmit={handleFeedbackSubmit}>
                <div className="aw-feedback-row">
                  <label htmlFor="feedback-type">Tipo</label>
                  <select
                    id="feedback-type"
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                  >
                    <option value="suggestion">Suggerimento</option>
                    <option value="issue">Problema</option>
                  </select>
                </div>
                <div className="aw-feedback-row">
                  <label htmlFor="feedback-priority">Priorita</label>
                  <select
                    id="feedback-priority"
                    value={feedbackPriority}
                    onChange={(e) => setFeedbackPriority(e.target.value)}
                  >
                    <option value="low">Bassa</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                  </select>
                </div>
                <div className="aw-feedback-row">
                  <label htmlFor="feedback-message">Messaggio</label>
                  <textarea
                    id="feedback-message"
                    rows={6}
                    maxLength={2000}
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Descrivi il suggerimento o il problema..."
                  />
                </div>
                {feedbackError && <p className="aw-feedback-error">{feedbackError}</p>}
                {feedbackSuccess && <p className="aw-feedback-success">{feedbackSuccess}</p>}
                <button type="submit" className="aw-feedback-submit" disabled={feedbackPending}>
                  {feedbackPending ? 'Invio...' : 'Invia feedback'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
